import SessionStore from 'stores/shared/session';
import getUserLocale from 'get-user-locale';

/**
 * Get the user's preferred locale with backwards compatibility
 *
 * This function implements a priority-based locale detection system
 * to maintain compatibility with legacy localization patterns:
 *
 * Priority Order:
 * 1. Query parameter (?lang=XX-YY) - Highest priority
 *    - Used by external integrations and language switching
 *    - Set by SessionLanguageChooser and external partners
 *
 * 2. SessionStore i18n value - Maintains state across pages
 *    - Set by Rails backend from session[:i18n]
 *    - Used by 22+ legacy components
 *
 * 3. Browser locale - Fallback to user's browser settings
 *    - Uses get-user-locale package (navigator.language)
 *    - Default when no explicit language preference set
 *
 * @returns The locale string (e.g., 'en-US', 'fr-FR', 'es-ES')
 *
 * @example
 * ```typescript
 * // URL: https://example.com/checkout?lang=fr-FR
 * getLegacyLocale() // Returns: 'fr-FR'
 *
 * // URL: https://example.com/checkout (no query param)
 * // SessionStore.state.get('i18n') = 'es-ES'
 * getLegacyLocale() // Returns: 'es-ES'
 *
 * // URL: https://example.com/checkout (no query param)
 * // SessionStore has no i18n value
 * // Browser language: fr-FR
 * getLegacyLocale() // Returns: 'fr-FR'
 * ```
 */
export const getLegacyLocale = (): string => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const queryLang = urlParams.get('lang');
    if (queryLang) {
      return queryLang;
    }
  } catch (error) {
    console.warn('[getLegacyLocale] Failed to parse query params:', error);
  }

  try {
    if (SessionStore?.state) {
      const sessionLang = SessionStore.state.get('i18n');
      // Only use sessionLang if it's set and not the default
      // This allows browser locale to be used for new sessions
      if (sessionLang && sessionLang !== 'en-US') {
        return sessionLang;
      }
    }
  } catch (error) {
    console.warn('[getLegacyLocale] Failed to read SessionStore:', error);
  }

  return getUserLocale();
};
